import { useState } from 'react';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { Navigator, SplitView, readStored, writeStored, type NavNode } from '@/components/desktop';
import { MasterPanel, type MasterKind } from './HrSetupLists';
import { PayrollRulesPage, TaxPage } from './HrSetupRules';
import './hr-admin.css';

type Section = MasterKind | 'rules' | 'tax';

const SECTION_KEY = 'poultry.hr.setup.section';

const SECTIONS: Array<{ id: Section; label: string; icon: IconKind; hint: string }> = [
  { id: 'departments', label: 'Departments', icon: 'department', hint: 'Groups of staff' },
  { id: 'designations', label: 'Designations', icon: 'designation', hint: 'Job titles' },
  { id: 'shifts', label: 'Shifts', icon: 'shift', hint: 'Working hours' },
  { id: 'pay-components', label: 'Pay items', icon: 'payroll', hint: 'What makes up pay' },
  { id: 'leave-types', label: 'Leave types', icon: 'leave', hint: 'Kinds of leave and days a year' },
  { id: 'rules', label: 'Payroll rules', icon: 'setup', hint: 'How pay is worked out' },
  { id: 'tax', label: 'Tax & government deductions', icon: 'tax', hint: 'Income tax, EOBI and PESSI for each year' },
];

/**
 * HR Setup: the lists everything else in HR picks from, the payroll rules,
 * and income tax, EOBI and PESSI for each tax year. Built like Items Manager —
 * a tree of sections on the left, the chosen one on the right.
 */
export function HrSetup() {
  const [section, setSection] = useState<Section>(() => {
    const stored = readStored<string>(SECTION_KEY, 'departments');
    return SECTIONS.some((s) => s.id === stored) ? (stored as Section) : 'departments';
  });

  const nodes: NavNode[] = SECTIONS.map((s) => ({ id: s.id, label: s.label, hint: s.hint, icon: <AppIcon kind={s.icon} /> }));

  return (
    <div className="workbench">
      <SplitView
        id="hr-setup"
        initial={220}
        firstLabel="HR setup sections"
        first={
          <Navigator
            id="hr-setup"
            label="HR Setup"
            nodes={nodes}
            selectedId={section}
            onSelect={(id) => {
              setSection(id as Section);
              writeStored(SECTION_KEY, id);
            }}
          />
        }
        second={
          section === 'rules' ? <PayrollRulesPage /> : section === 'tax' ? <TaxPage /> : <MasterPanel key={section} kind={section} />
        }
      />
    </div>
  );
}
